import { useState } from 'react';
import type { AtCapPolicy } from '@salonomy/types';
import { Card, Pill, SegmentedControl } from '@salonomy/ui';
import { useCommsAllowance, useUpdateCommsPolicy } from '../data/comms.js';
import { CampaignComposer, MessageHistory, TemplatesPanel } from '../features/comms/index.js';
import { useAuth } from '../lib/auth.js';
import { money } from '../lib/format.js';
import '../features/comms/comms.css';

type CommsTab = 'compose' | 'history' | 'templates';

const TABS: Array<{ value: CommsTab; label: string }> = [
  { value: 'compose', label: 'Campaigns' },
  { value: 'history', label: 'Message history' },
  { value: 'templates', label: 'Templates' },
];

const POLICIES: Array<{ value: AtCapPolicy; title: string; blurb: string }> = [
  {
    value: 'pause',
    title: 'Pause & switch to free',
    blurb: 'Stop paid messages at the cap and keep sending free email + push. We never auto-spend.',
  },
  {
    value: 'ask',
    title: 'Ask first',
    blurb: 'Hold paid messages at the cap and ask an owner before sending more.',
  },
  {
    value: 'auto_top_up',
    title: 'Auto top-up',
    blurb:
      'Automatically buy more allowance at the published rate. You opt in — it’s never the default.',
  },
];

function Meter({
  label,
  used,
  allowance,
  unitCostPence,
}: {
  label: string;
  used: number;
  allowance: number;
  unitCostPence: number;
}) {
  const pct = allowance > 0 ? Math.min(100, Math.round((used / allowance) * 100)) : 0;
  const atCap = used >= allowance && allowance > 0;
  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontWeight: 600 }}>{label}</span>
        <span className="mono" style={{ fontSize: 13 }}>
          {used} / {allowance}
        </span>
      </div>
      <div className="meter" style={{ marginTop: 12 }}>
        <div
          className={`meter__fill${atCap ? ' meter__fill--cap' : ''}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
        {money(unitCostPence)} per segment · overage published, never hidden
        {atCap ? ' · at cap' : ''}
      </div>
    </Card>
  );
}

export function CommsPage() {
  const { data: comms } = useCommsAllowance();
  const updatePolicy = useUpdateCommsPolicy();
  const { isManager } = useAuth();
  const [tab, setTab] = useState<CommsTab>('compose');

  return (
    <>
      <div className="topbar">
        <div className="topbar__left">
          <div className="topbar__title">Comms</div>
          <span className="muted">
            Honest economics — unlimited free channels, capped paid ones
          </span>
        </div>
      </div>

      <div className="screen-scroll">
        <div
          className="screen-pad"
          style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 920 }}
        >
          <div className="comms-grid">
            <Card>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span style={{ fontWeight: 600 }}>Email &amp; push</span>
                <Pill tone="success">Always free</Pill>
              </div>
              <div className="muted" style={{ fontSize: 13, marginTop: 12, lineHeight: 1.5 }}>
                Unlimited reminders, confirmations and campaigns over email and app push — no cap,
                no credits, no per-message cost.
              </div>
            </Card>

            <Meter
              label="SMS"
              used={comms?.smsUsed ?? 0}
              allowance={comms?.smsAllowance ?? 0}
              unitCostPence={6}
            />
            <Meter
              label="WhatsApp"
              used={comms?.whatsappUsed ?? 0}
              allowance={comms?.whatsappAllowance ?? 0}
              unitCostPence={6}
            />
          </div>

          {comms ? (
            <Card>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>
                    Smart routing saved you {money(comms.smartRoutingSavedPence)}
                  </div>
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    This period — by preferring free email/push before paid SMS where it still
                    reaches the client.
                  </div>
                </div>
                <Pill tone="soft">{comms.chairs} chairs · pooled</Pill>
              </div>
            </Card>
          ) : null}

          <div>
            <div className="section-title">When the cap is reached</div>
            <div className="comms-policies">
              {POLICIES.map((p) => {
                const active = comms?.atCapPolicy === p.value;
                // Changing the at-cap policy is an owner/manager action (the API gates
                // PUT /comms/policy on requireManager). Render the pills read-only for everyone
                // else rather than present a control that is guaranteed to 403 silently.
                const className = `policy${active ? ' policy--active' : ''}${
                  isManager ? '' : ' policy--readonly'
                }`;
                const content = (
                  <>
                    <div className="policy__title">
                      {p.title}
                      {active ? <Pill tone="cobalt">Selected</Pill> : null}
                    </div>
                    <div className="policy__blurb">{p.blurb}</div>
                  </>
                );
                return isManager ? (
                  <button
                    key={p.value}
                    className={className}
                    onClick={() => updatePolicy.mutate({ atCapPolicy: p.value })}
                  >
                    {content}
                  </button>
                ) : (
                  <div key={p.value} className={className} aria-disabled="true">
                    {content}
                  </div>
                );
              })}
            </div>
            {!isManager ? (
              <div className="muted" style={{ fontSize: 12.5, marginTop: 10, lineHeight: 1.5 }}>
                Changing the at-cap policy is an owner/manager action. Ask an owner or manager to
                change how paid messages behave at the cap.
              </div>
            ) : null}
          </div>

          {/* ── Send pipeline (campaigns / history / templates) beneath the live allowance + policy ── */}
          <div>
            <div className="section-title">Send</div>
            <div className="comms-tabs">
              <SegmentedControl options={TABS} value={tab} onChange={setTab} />
            </div>
          </div>

          {tab === 'compose' ? (
            <CampaignComposer canManage={isManager} />
          ) : tab === 'history' ? (
            <MessageHistory />
          ) : (
            <TemplatesPanel canManage={isManager} />
          )}
        </div>
      </div>
    </>
  );
}
